/**
 * Stunden-Leiste (Gestaltungsentscheid Signage S1 21.10.2026, H-C oben; Design-Guide `stunden-leiste.md`): die letzten 24 Stunden
 * als festes 24er-Raster (`grid-cols-24`, keine Breiten per `style=`), je Stunde ein Segment in Theme-Tönen. Für Gerätezustände
 * (online/teilweise/offline), nie für Personen. Rein darstellend; die Stufen rechnet der Aufrufer.
 */
export type StundenStufe = "online" | "teilweise" | "offline" | "leer";

export interface StundenSegment {
  /** Stunde des Segments (0–23, Ortszeit) für Beschriftung und Hilfstechnik. */
  stunde: number;
  stufe: StundenStufe;
}

const SEGMENT: Record<StundenStufe, string> = {
  online: "h-8 rounded-sm bg-status-ok",
  teilweise: "h-8 rounded-sm bg-status-warn",
  offline: "h-8 rounded-sm bg-surface-3",
  leer: "h-8 rounded-sm bg-surface",
};

const PUNKT: Record<Exclude<StundenStufe, "leer">, string> = {
  online: "shrink-0 size-2.5 inline-block rounded-sm bg-status-ok",
  teilweise: "shrink-0 size-2.5 inline-block rounded-sm bg-status-warn",
  offline: "shrink-0 size-2.5 inline-block rounded-sm bg-surface-3",
};

const STUFE_TEXT: Record<StundenStufe, string> = {
  online: "online",
  teilweise: "mit Lücken",
  offline: "keine Meldung",
  leer: "noch nicht gekoppelt",
};

function zweistellig(n: number): string {
  return String(n).padStart(2, "0");
}

export function StundenLeiste({
  id,
  titel,
  segmente,
  summe,
}: {
  id: string;
  titel: string;
  /** Genau 24 Segmente, älteste zuerst. */
  segmente: readonly StundenSegment[];
  /** Satz unter der Leiste, z. B. „23 von 24 Stunden online“. */
  summe: string;
}) {
  const titelId = `${id}-titel`;
  const erste = segmente[0];
  const letzte = segmente[segmente.length - 1];
  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-progress-cards/segmented-status-progress-card */}
      {/* Abweichung vom Block: feste 24 Stunden-Segmente statt Anteilsbreiten (kein style=), Theme-Töne statt Roh-Paletten (Gestaltungsentscheid S1). */}
      <section aria-labelledby={titelId} className="p-5 flex flex-col bg-card border border-card-line shadow-xs rounded-xl">
        <div className="pb-4 flex flex-wrap justify-between items-center gap-2">
          <h2 id={titelId} className="font-medium text-foreground">
            {titel}
          </h2>
          <p className="text-sm text-muted-foreground-1">{summe}</p>
        </div>
        <ol className="grid grid-cols-24 gap-0.5" aria-label={`${titel}, je Stunde`}>
          {segmente.map((s, i) => (
            <li key={`${s.stunde}-${i}`} className={SEGMENT[s.stufe]} title={`${zweistellig(s.stunde)}:00 ${STUFE_TEXT[s.stufe]}`}>
              <span className="sr-only">
                {zweistellig(s.stunde)}:00 Uhr: {STUFE_TEXT[s.stufe]}
              </span>
            </li>
          ))}
        </ol>
        <div className="mt-2 flex justify-between text-xs text-muted-foreground-1" aria-hidden="true">
          <span>{erste ? `${zweistellig(erste.stunde)}:00` : ""}</span>
          <span>{letzte ? "jetzt" : ""}</span>
        </div>
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground-1">
          {(["online", "teilweise", "offline"] as const).map((st) => (
            <li key={st} className="inline-flex items-center gap-x-1.5">
              <span className={PUNKT[st]} aria-hidden="true" />
              {STUFE_TEXT[st]}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
